import React, { useState } from 'react';
import { ItemsDto } from '../../../../types/items';
import {
  ItemTypeChangePreview,
  ItemTypeChangeStrategy,
  ItemTypeChangeManualEntry,
} from '../../../../types/itemTypeChange';
import { BackButton } from '../../../common/BackButton';
import { Step1SelectItem } from './Step1SelectItem';
import { Step2SelectNewType } from './Step2SelectNewType';
import { Step3PreviewConfirm } from './Step3PreviewConfirm';
import { Shuffle } from 'lucide-react';

interface ItemTypeChangeProps {
  onBack?: () => void;
}

type Step = 1 | 2 | 3;

export const ItemTypeChange: React.FC<ItemTypeChangeProps> = ({ onBack }) => {
  const [step, setStep] = useState<Step>(1);
  const [selectedItem, setSelectedItem] = useState<ItemsDto | null>(null);
  const [newItemType, setNewItemType] = useState<number | null>(null);
  const [preview, setPreview] = useState<ItemTypeChangePreview | null>(null);
  const [strategy, setStrategy] = useState<ItemTypeChangeStrategy | null>(null);
  const [manualEntries, setManualEntries] = useState<ItemTypeChangeManualEntry[]>([]);
  const [reason, setReason] = useState<string>('');

  const reset = () => {
    setSelectedItem(null);
    setNewItemType(null);
    setPreview(null);
    setStrategy(null);
    setManualEntries([]);
    setReason('');
    setStep(1);
  };

  return (
    <div className="min-h-full p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          {onBack && <BackButton onClick={onBack} label="Tilbake" />}
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <div className="flex items-start gap-3 mb-6">
            <div className="bg-red-100 rounded-full p-3 flex-shrink-0">
              <Shuffle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Bytt varetype</h1>
              <p className="text-sm text-gray-600 mt-1">
                Kontrollert endring av Items.ItemType med migrering av ItemBins-data.
                Alle endringer logges i ApplicationLogs.
              </p>
            </div>
          </div>

          {/* Progress indikator */}
          <div className="flex items-center justify-between mb-8 px-4">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      step >= s ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {s}
                  </div>
                  <span className={`mt-1 text-xs ${step >= s ? 'text-gray-800' : 'text-gray-400'}`}>
                    {s === 1 ? 'Velg vare' : s === 2 ? 'Velg ny type' : 'Gjennomgang'}
                  </span>
                </div>
                {s < 3 && (
                  <div className={`flex-1 h-0.5 ${step > s ? 'bg-red-600' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Stegvisning */}
          {step === 1 && (
            <Step1SelectItem
              onItemSelected={(item) => {
                setSelectedItem(item);
                setStep(2);
              }}
            />
          )}

          {step === 2 && selectedItem && (
            <Step2SelectNewType
              item={selectedItem}
              onPreviewReady={(newType, previewResult) => {
                setNewItemType(newType);
                setPreview(previewResult);
                setStrategy(previewResult.defaultStrategy);
                setStep(3);
              }}
              onBack={() => setStep(1)}
            />
          )}

          {step === 3 && selectedItem && newItemType !== null && preview && strategy !== null && (
            <Step3PreviewConfirm
              item={selectedItem}
              preview={preview}
              strategy={strategy}
              onStrategyChange={setStrategy}
              manualEntries={manualEntries}
              onManualEntriesChange={setManualEntries}
              reason={reason}
              onReasonChange={setReason}
              onBack={() => setStep(2)}
              onDone={reset}
            />
          )}
        </div>
      </div>
    </div>
  );
};
